import { useState } from 'react'
import type { Card } from '../../types'
import { motion } from 'framer-motion'
import { Video } from 'lucide-react'
import { getScaledIconSize } from '../../utils/gridLayout'

interface VideoCardProps {
  card: Card
  editMode: boolean
  cellSize: number
}

function getYouTubeId(url: string): string | null {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  return match && match[2].length === 11 ? match[2] : null
}

export default function VideoCard({ card, editMode, cellSize }: VideoCardProps) {
  const { videoUrl } = card.content
  const iconSize = getScaledIconSize(cellSize, 32)
  const [playing, setPlaying] = useState(false)

  const ytId = videoUrl ? getYouTubeId(videoUrl) : null
  const thumbUrl = ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (editMode) return
    if (ytId) setPlaying(true)
  }

  if (playing && ytId) {
    return (
      <motion.div
        className="w-full h-full theme-card theme-card-hover overflow-hidden"
        whileHover={{ borderColor: 'rgba(0,0,0,0.25)' }}
      >
        <iframe
          src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1`}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </motion.div>
    )
  }

  return (
    <motion.div
      className="w-full h-full theme-card theme-card-hover overflow-hidden relative cursor-pointer"
      whileHover={{ borderColor: 'rgba(0,0,0,0.25)' }}
      onClick={handlePlay}
    >
      {thumbUrl ? (
        <>
          <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="w-12 h-12 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Video size={iconSize} className="opacity-70" />
        </div>
      )}
    </motion.div>
  )
}
